import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, Filter, Save, X, Trash2, Plus, RotateCcw, CheckCircle2, AlertTriangle, Edit3 } from "lucide-react";

const MODULE_LABELS = {
  1: "Federal Law", 2: "SAFE Act", 3: "Loan Products", 4: "MLO Activities", 5: "Ethics & Fraud",
};

export default function AdminQuestionsEditor() {
  const [modules, setModules] = useState([]);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [editingQn, setEditingQn] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [tab, setTab] = useState("module"); // module | sample

  async function load() {
    setLoading(true);
    const { data } = await api.get("/admin/questions");
    setModules(data.module_questions);
    setSamples(data.sample_questions);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const list = tab === "module" ? modules : samples;
    return list.filter((q) => {
      if (tab === "module" && moduleFilter !== "all" && q.module !== parseInt(moduleFilter)) return false;
      if (query) {
        const s = query.toLowerCase();
        if (!q.q.toLowerCase().includes(s) && !q.qn.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [tab, modules, samples, moduleFilter, query]);

  return (
    <div data-testid="admin-questions-editor">
      {/* Sub-tabs */}
      <div className="flex gap-2 mb-5">
        <button onClick={() => setTab("module")} data-testid="q-tab-module" className={`px-3 py-1.5 text-xs rounded-sm border ${tab === "module" ? "border-[var(--dw-navy)] bg-[var(--dw-navy)] text-[var(--dw-parchment)]" : "border-[var(--dw-navy)]/15 text-neutral-600 hover:border-[var(--dw-navy)]/40"}`}>
          Module Quiz Questions ({modules.length})
        </button>
        <button onClick={() => setTab("sample")} data-testid="q-tab-sample" className={`px-3 py-1.5 text-xs rounded-sm border ${tab === "sample" ? "border-[var(--dw-navy)] bg-[var(--dw-navy)] text-[var(--dw-parchment)]" : "border-[var(--dw-navy)]/15 text-neutral-600 hover:border-[var(--dw-navy)]/40"}`}>
          Sample Test Questions ({samples.length})
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search question text or ID (Q1, ST3)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="q-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)]"
          />
        </div>
        {tab === "module" && (
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            data-testid="q-module-filter"
            className="text-sm border border-[var(--dw-navy)]/15 rounded-sm px-3 py-2"
          >
            <option value="all">All modules</option>
            {[1, 2, 3, 4, 5].map((m) => <option key={m} value={m}>M{m} — {MODULE_LABELS[m]}</option>)}
          </select>
        )}
        {tab === "module" && (
          <button
            onClick={() => setCreatingNew(true)}
            data-testid="q-new-btn"
            className="dw-btn-gold rounded-sm px-4 py-2 text-sm inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Add question
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-neutral-500 py-10 text-center">Loading questions…</div>
      ) : (
        <div className="space-y-2">
          {filtered.length === 0 && (
            <div className="text-neutral-500 py-10 text-center text-sm">No questions match your filters.</div>
          )}
          {filtered.map((q) => (
            <QuestionRow
              key={q.qn}
              q={q}
              tab={tab}
              editing={editingQn === q.qn}
              onEdit={() => setEditingQn(q.qn)}
              onClose={() => setEditingQn(null)}
              onSaved={load}
            />
          ))}
        </div>
      )}

      {creatingNew && tab === "module" && (
        <NewQuestionModal
          onClose={() => setCreatingNew(false)}
          onCreated={() => { setCreatingNew(false); load(); }}
        />
      )}
    </div>
  );
}

function QuestionRow({ q, tab, editing, onEdit, onClose, onSaved }) {
  const [form, setForm] = useState(() => cloneQ(q, tab));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (editing) setForm(cloneQ(q, tab)); }, [editing, q, tab]);

  async function save() {
    setSaving(true);
    try {
      const url = tab === "sample" ? `/admin/sample-questions/${q.qn}` : `/admin/questions/${q.qn}`;
      const payload = tab === "sample"
        ? { q: form.q, choices: form.choices, correct: form.correct, explanation: form.explanation, topic: form.topic }
        : { q: form.q, choices: form.choices, ans: form.ans, explanation: form.explanation, module: form.module };
      await api.put(url, payload);
      onSaved();
      onClose();
    } finally { setSaving(false); }
  }

  async function del() {
    if (!window.confirm(`Delete ${q.qn}? Students will no longer see this question.`)) return;
    await api.delete(`/admin/questions/${q.qn}`);
    onSaved();
  }

  async function reset() {
    if (!window.confirm(`Reset ${q.qn} to the original seed content? Your edits will be lost.`)) return;
    const url = tab === "sample" ? `/admin/sample-questions/${q.qn}/reset` : `/admin/questions/${q.qn}/reset`;
    await api.post(url);
    onSaved();
  }

  const correctLetter = tab === "sample" ? q.correct : q.ans;

  if (!editing) {
    return (
      <div className="border border-[var(--dw-navy)]/12 rounded-sm p-4 bg-white group" data-testid={`q-row-${q.qn}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono-plex text-xs text-neutral-500">{q.qn}</span>
              {q.module && <span className="text-[10px] px-1.5 py-0.5 border border-[var(--dw-navy)]/15 rounded-sm text-neutral-600">M{q.module}</span>}
              {tab === "sample" && q.topic && <span className="text-[10px] px-1.5 py-0.5 border border-[var(--dw-navy)]/15 rounded-sm text-neutral-600">{q.topic}</span>}
              {q._edited && <span className="text-[10px] px-1.5 py-0.5 border border-amber-400 bg-amber-50 text-amber-800 rounded-sm inline-flex items-center gap-1"><Edit3 className="w-2.5 h-2.5" /> Edited</span>}
              {q._custom && <span className="text-[10px] px-1.5 py-0.5 border border-emerald-400 bg-emerald-50 text-emerald-800 rounded-sm">Custom</span>}
              <span className="text-[10px] font-mono-plex text-neutral-500 ml-auto">Correct: {correctLetter}</span>
            </div>
            <div className="text-sm text-[var(--dw-navy)] leading-snug">{q.q}</div>
          </div>
          <div className="flex gap-1 shrink-0">
            {q._edited && (
              <button onClick={reset} title="Reset to default" className="w-8 h-8 flex items-center justify-center border border-[var(--dw-navy)]/12 hover:border-amber-500 rounded-sm text-neutral-500 hover:text-amber-600" data-testid={`q-reset-${q.qn}`}>
                <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            )}
            <button onClick={onEdit} className="w-8 h-8 flex items-center justify-center border border-[var(--dw-navy)]/12 hover:border-[var(--dw-navy)] rounded-sm text-neutral-500 hover:text-[var(--dw-navy)]" data-testid={`q-edit-${q.qn}`}>
              <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
            {tab === "module" && (
              <button onClick={del} className="w-8 h-8 flex items-center justify-center border border-[var(--dw-navy)]/12 hover:border-red-500 rounded-sm text-neutral-500 hover:text-red-600" data-testid={`q-del-${q.qn}`}>
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-2 border-[var(--dw-gold)] rounded-sm p-4 bg-[var(--dw-parchment)]" data-testid={`q-edit-${q.qn}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-serif text-lg text-[var(--dw-navy)]">Editing {q.qn}</div>
        <button onClick={onClose} className="text-neutral-500 hover:text-[var(--dw-navy)]"><X className="w-4 h-4" strokeWidth={1.5} /></button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Question</label>
          <textarea
            value={form.q}
            onChange={(e) => setForm({ ...form, q: e.target.value })}
            rows={2}
            data-testid="q-field-question"
            className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)] font-sans"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {["A", "B", "C", "D"].map((letter) => (
            <div key={letter}>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
                Choice {letter}
                {(tab === "sample" ? form.correct : form.ans) === letter && <span className="ml-1.5 text-emerald-700">(correct)</span>}
              </label>
              <input
                value={form.choices[letter] || ""}
                onChange={(e) => setForm({ ...form, choices: { ...form.choices, [letter]: e.target.value } })}
                data-testid={`q-field-choice-${letter}`}
                className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)]"
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Correct answer</label>
            <select
              value={tab === "sample" ? form.correct : form.ans}
              onChange={(e) => setForm(tab === "sample" ? { ...form, correct: e.target.value } : { ...form, ans: e.target.value })}
              data-testid="q-field-correct"
              className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm"
            >
              {["A", "B", "C", "D"].map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          {tab === "module" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Module</label>
              <select value={form.module} onChange={(e) => setForm({ ...form, module: parseInt(e.target.value) })} data-testid="q-field-module" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm">
                {[1, 2, 3, 4, 5].map((m) => <option key={m} value={m}>M{m} — {MODULE_LABELS[m]}</option>)}
              </select>
            </div>
          )}
          {tab === "sample" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Topic</label>
              <input value={form.topic || ""} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="q-field-topic" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Explanation (shown after student answers)</label>
          <textarea
            value={form.explanation || ""}
            onChange={(e) => setForm({ ...form, explanation: e.target.value })}
            rows={2}
            data-testid="q-field-explanation"
            className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)]"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-sm text-neutral-600 hover:text-[var(--dw-navy)] px-4 py-2">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="q-save-btn" className="dw-btn-gold rounded-sm px-5 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
          <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function NewQuestionModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    q: "", choices: { A: "", B: "", C: "", D: "" }, ans: "A", explanation: "", module: 1,
  });
  const [saving, setSaving] = useState(false);

  async function create() {
    if (!form.q || !form.choices.A || !form.choices.B) {
      alert("Question and at least choices A and B are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/questions", form);
      onCreated();
    } catch (e) {
      alert("Failed to create: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="q-new-modal">
      <div className="max-w-2xl w-full bg-[var(--dw-parchment)] border border-[var(--dw-navy)]/20 rounded-sm shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-[var(--dw-navy)]/10 px-5 py-3">
          <div className="font-serif text-xl text-[var(--dw-navy)]">New question</div>
          <button onClick={onClose}><X className="w-4 h-4 text-neutral-500 hover:text-[var(--dw-navy)]" strokeWidth={1.5} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Question</label>
            <textarea value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} rows={2} data-testid="q-new-question" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
          </div>
          {["A", "B", "C", "D"].map((letter) => (
            <div key={letter}>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Choice {letter}</label>
              <input value={form.choices[letter]} onChange={(e) => setForm({ ...form, choices: { ...form.choices, [letter]: e.target.value } })} data-testid={`q-new-choice-${letter}`} className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Correct</label>
              <select value={form.ans} onChange={(e) => setForm({ ...form, ans: e.target.value })} data-testid="q-new-correct" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm">
                {["A", "B", "C", "D"].map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Module</label>
              <select value={form.module} onChange={(e) => setForm({ ...form, module: parseInt(e.target.value) })} data-testid="q-new-module" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm">
                {[1, 2, 3, 4, 5].map((m) => <option key={m} value={m}>M{m} — {MODULE_LABELS[m]}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Explanation</label>
            <textarea value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} rows={2} data-testid="q-new-explanation" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[var(--dw-navy)]/10 px-5 py-3">
          <button onClick={onClose} className="text-sm text-neutral-600 hover:text-[var(--dw-navy)] px-4 py-2">Cancel</button>
          <button onClick={create} disabled={saving} data-testid="q-new-create" className="dw-btn-gold rounded-sm px-5 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
            <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Creating…" : "Create question"}
          </button>
        </div>
      </div>
    </div>
  );
}

function cloneQ(q, tab) {
  return {
    q: q.q,
    choices: { A: "", B: "", C: "", D: "", ...q.choices },
    ans: q.ans,
    correct: q.correct,
    explanation: q.explanation || "",
    module: q.module,
    topic: q.topic || "",
  };
}
